import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CountryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useTaxOfficeMutations } from '~/hooks/use-tax-offices';
import { taxOfficeSchema, EMPTY_TAX_OFFICE, type TaxOfficeFormValues } from '~/lib/schemas/tax-office';

const NO_COUNTRY = '__none__';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: TaxOfficeFormValues | null;
	countries: CountryToken[];
}

/** Add / edit a tax office (ΔΟΥ). */
export function TaxOfficeEditDialog({ open, onOpenChange, initial, countries }: Props) {
	const { t } = useTranslation();
	const { create, update } = useTaxOfficeMutations();
	const form = useForm<TaxOfficeFormValues>({
		resolver: zodResolver(taxOfficeSchema) as Resolver<TaxOfficeFormValues>,
		defaultValues: initial ?? EMPTY_TAX_OFFICE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_TAX_OFFICE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const cid = watch('cid');
	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.taxOffices.editTitle') : t('system.taxOffices.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.taxOffices.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.taxOffices.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} autoFocus />
						</FormField>
						<FormField label={t('system.taxOffices.columns.country')}>
							<Select
								value={cid === null ? NO_COUNTRY : String(cid)}
								onValueChange={v => setValue('cid', v === NO_COUNTRY ? null : Number(v), { shouldDirty: true })}>
								<SelectTrigger className="w-full" aria-label={t('system.taxOffices.columns.country')}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NO_COUNTRY}>{t('system.taxOffices.noCountry')}</SelectItem>
									{countries.map(c => (
										<SelectItem key={c.id} value={String(c.id)}>
											{c.nm}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>
					</div>
					<FormField label={t('system.taxOffices.columns.description')} error={formState.errors.dsc?.message}>
						<Input {...register('dsc')} maxLength={100} aria-invalid={Boolean(formState.errors.dsc)} />
					</FormField>
					<FormField label={t('system.taxOffices.columns.address')} error={formState.errors.adr?.message}>
						<Input {...register('adr')} maxLength={200} aria-invalid={Boolean(formState.errors.adr)} />
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.taxOffices.columns.city')} error={formState.errors.ct?.message}>
							<Input {...register('ct')} maxLength={50} aria-invalid={Boolean(formState.errors.ct)} />
						</FormField>
						<FormField label={t('system.taxOffices.columns.postalCode')} error={formState.errors.pc?.message}>
							<Input {...register('pc')} maxLength={20} aria-invalid={Boolean(formState.errors.pc)} />
						</FormField>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.fields.phone')}>
							<Input {...register('tel')} />
						</FormField>
						<FormField label={t('system.fields.erpCode')}>
							<Input {...register('erpcd')} />
						</FormField>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.taxOffices.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
						<div className="flex items-end">
							<div className="flex w-full items-center justify-between rounded-md border border-border px-4 py-2">
								<Label htmlFor="tax-visible">{t('system.taxOffices.columns.visible')}</Label>
								<Switch id="tax-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
